import { useEffect, useRef, useState } from "react";
import { Nullable } from "../ts-types";

type Options = {
  threshold: number;
  root: Nullable<Element>;
  rootMargin: string;
  triggerOnce: boolean;
  onEnter?: () => void;
  onExit?: () => void;
};

const useInViewport = ({
  threshold = 0.1,
  root = null,
  rootMargin = "0px",
  triggerOnce = false,
  onEnter,
  onExit
}: Partial<Options> = {}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isInViewport, setIsInViewport] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const observerRef = useRef<Nullable<IntersectionObserver>>(null);

  useEffect(() => {
    const element = ref.current as HTMLDivElement | null;
    if (!element) return;

    // If triggerOnce is enabled and element has already been visible, skip observation
    if (triggerOnce && hasBeenVisible) {
      return;
    }

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        const isIntersecting = entry.isIntersecting;

        setIsInViewport(isIntersecting);

        if (isIntersecting) {
          setHasBeenVisible(true);

          if (onEnter) {
            onEnter();
          }

          // If triggerOnce is enabled, disconnect observer after first trigger
          if (triggerOnce && observerRef.current) {
            observerRef.current.disconnect();
          }
        } else if (onExit && !triggerOnce) {
          // Only call onExit if not in triggerOnce mode
          onExit();
        }
      });
    };

    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold,
      root,
      rootMargin
    });

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [threshold, root, rootMargin, triggerOnce, onEnter, onExit]);

  return {
    ref,
    isInViewport,
    hasBeenVisible
  };
};

export default useInViewport;
